// @flow
import { createRef, useState } from "react";
import { css } from "styled-components";
import {
  Modal,
  TextInput,
  largeHeading,
  media,
  regularHeading,
  mediumParagraph,
} from "@nested/brand";
import { Button, ButtonLink } from "@nested/component-library";
import { Heading, QuestionDescription, QuestionWrapper } from "../components";
import { type QuestionProps } from "../components/MultiStageForm";
import { useDesktopFocus } from "../hooks/useDesktopFocus";

import {
  composeValidators,
  validateLength,
  validatePresence,
} from "./validators";

const validate = composeValidators(
  validatePresence("phone", "Please enter a valid phone number"),
  validateLength("phone", 2, 20, "Please enter a valid phone number"),
);

const headingStyle = css`
  ${regularHeading}
  margin-top: 0px;

  ${media.desktop`
    ${largeHeading}
    font-size: 40px;
  `}
`;

export const PhoneNumber = ({
  ActionButtons,
  BackButton,
  initialValue,
  next,
  valid,
  validationErrors,
}: QuestionProps<"phone">) => {
  const [phoneNumber, setPhoneNumber] = useState(initialValue || "");
  const [modalOpen, setModalOpen] = useState(false);

  const ref = createRef();
  useDesktopFocus(ref);

  return (
    <>
      <QuestionWrapper
        next={() => next(phoneNumber, validate)}
        dataTest="telephone-question"
      >
        <BackButton />
        <Heading>What's your phone number?</Heading>
        <QuestionDescription>
          So your local Nested agent can get in touch to discuss the property
          market and your sale.
        </QuestionDescription>

        <TextInput
          className="fs-exclude"
          data-test="onboarding-form:telephone"
          errorMessage={validationErrors.phone}
          hasErrors={!valid}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="Phone number"
          ref={ref}
          type="tel"
          value={phoneNumber}
        />
        <ActionButtons
          skip={(e) => {
            e.preventDefault();
            setModalOpen(true);
          }}
        />
      </QuestionWrapper>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <h1 css={headingStyle}>
          We'll arrange your home visit over the phone.
        </h1>
        <p css={mediumParagraph}>
          We'll only use your number to arrange a free home valuation. We won't
          ever share your details without your consent.
        </p>
        <Button
          size="large"
          type="primary"
          css="margin-right: 10px; margin-bottom: 10px;"
          onClick={() => setModalOpen(false)}
        >
          OK, continue!
        </Button>
        <ButtonLink size="large" css="margin-bottom: 10px;" to="/how-it-works">
          Find out more
        </ButtonLink>
      </Modal>
    </>
  );
};
